/**
 * Shared product-card add/undo, progressively enhancing native Shopify forms.
 * Liquid opts in default variants without selling plans or quantity constraints.
 * One click adds one unit; the next undoes that unit using its exact cart line.
 * A delegated singleton survives Theme Editor updates. Shopify retains ownership
 * of native cart analytics; this component does not emit duplicate GA4 events.
 */
(() => {
  'use strict';
  if (window.ETReimaginedQuickAdd) return;
  window.ETReimaginedQuickAdd = true;
  const pending = new WeakSet();
  const states = new WeakMap();
  // Serialise this component's operations across cards. In particular, two undo
  // clicks must not both subtract from the same stale cart quantity.
  let cartQueue = Promise.resolve();

  /** Refresh from a returned cart, never increment a stale header count. */
  function updateCount(cart) {
    if (!Number.isInteger(cart.item_count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });
  }

  /** Preserve Shopify's useful stock errors as plain text, never HTML. */
  async function request(root, path, options = {}) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      ...options,
    });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error('Cart request failed');
      error.shopifyMessage = typeof result.description === 'string' ? result.description : '';
      throw error;
    }
    return result;
  }

  /** A malformed cart is not permission to change an arbitrary variant/line. */
  function requireCart(cart) {
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) {
      throw new Error('Cart response could not be verified');
    }
    return cart;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-etr-quick-add]')) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (pending.has(form) || button.disabled) return;
    pending.add(form);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    if (!states.has(form)) {
      states.set(form, { addLabel: button.getAttribute('aria-label'), receipt: null });
    }
    const state = states.get(form);
    const undo = Boolean(state.receipt);
    const label = form.querySelector('[data-etr-add-label]');
    const status = form.querySelector('[data-etr-cart-status]');
    const cartLink = form.querySelector('[data-etr-view-cart]');
    label.textContent = undo ? form.dataset.removing : form.dataset.adding;
    status.textContent = '';
    cartLink.hidden = true;
    const root = window.Shopify?.routes?.root || '/';

    /** Confirmed feedback stays inside the button; the live region is visually
     * hidden so assistive technology hears the result without moving the cards. */
    function confirm(added) {
      form.dataset.cartState = added ? 'added' : 'idle';
      form.dataset.cartFeedback = 'success';
      label.textContent = added ? form.dataset.added : form.dataset.add;
      button.setAttribute('aria-label', added ? form.dataset.undoLabel : state.addLabel);
      button.setAttribute('aria-pressed', String(added));
      if (added) button.setAttribute('title', form.dataset.undoHint);
      else button.removeAttribute('title');
      status.textContent = added ? form.dataset.added : form.dataset.removed;
      button.disabled = false;
      cartLink.hidden = true;
    }

    const operation = cartQueue.then(async () => {
      let mutationStarted = false;
      try {
        if (undo) {
          const cart = requireCart(await request(root, 'cart.js', { cache: 'no-store' }));
          const line = cart.items.find((item) => item.key === state.receipt.key);
          if (!line) {
            // Discounts/properties can change line keys. Never guess by variant
            // when another line still exists: direct the shopper to their cart.
            if (cart.items.some((item) => item.variant_id === state.receipt.variantId)) {
              throw new Error('Cart line identity changed');
            }
            // The item was already removed elsewhere; there is nothing to undo.
            state.receipt = null;
            updateCount(cart);
            confirm(false);
            return;
          }
          if (!Number.isInteger(line.quantity) || line.quantity < 1) {
            throw new Error('Cart quantity could not be verified');
          }
          mutationStarted = true;
          const updated = requireCart(await request(root, 'cart/change.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: line.quantity - 1 }),
          }));
          // Subtract only the card's one added unit, preserving existing units.
          state.receipt = null;
          updateCount(updated);
          confirm(false);
        } else {
          mutationStarted = true;
          const result = await request(root, 'cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          const item = Array.isArray(result.items) ? result.items[0] : result;
          if (typeof item?.key !== 'string' || !Number.isInteger(item.id)) {
            throw new Error('Added line identity could not be verified');
          }
          state.receipt = { key: item.key, variantId: item.id };
          confirm(true);
          // Keep the button busy until the count read finishes. A failed read
          // cannot invalidate a confirmed add or cause an automatic retry.
          button.disabled = true;
          try {
            updateCount(requireCart(await request(root, 'cart.js', { cache: 'no-store' })));
          } catch (_) { /* The confirmed add and its undo remain available. */ }
          button.disabled = false;
        }
      } catch (error) {
        form.dataset.cartFeedback = 'error';
        status.textContent = error.shopifyMessage || form.dataset.error;
        cartLink.hidden = false;
        if (undo && !mutationStarted) {
          // A failed preflight sent no write, so retrying this undo is safe.
          label.textContent = form.dataset.added;
          button.disabled = false;
        } else {
          // A lost write response has an unknown outcome. Never retry it or
          // reverse it automatically; ask the shopper to inspect their cart.
          form.dataset.cartState = 'error';
          label.textContent = cartLink.textContent;
          button.setAttribute('aria-label', cartLink.textContent);
          button.removeAttribute('aria-pressed');
          button.removeAttribute('title');
        }
      } finally {
        button.removeAttribute('aria-busy');
        pending.delete(form);
      }
    });
    cartQueue = operation.catch(() => {});
    await operation;
  });
})();
